import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, RecordIdSchema } from './identifiers';
import {
  DecimalStringSchema,
  MoneySchema,
  divideMoney,
  isZeroMoney,
  multiplyMoney,
  type Money,
} from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Budgets — BMP-2 package 5 (`FIN-BUDGET-001…003`, the budget check of `PROC-REQUEST-001`).
 *
 * A budget belongs to one legal entity and one fiscal year, and is held for one dimension: the whole
 * company, a project, a department, a marketing campaign or a cost centre. Its figures live in
 * **versions**: a draft is prepared, submitted, approved (through the engine where a policy governs
 * `finance.budget.approve`) and becomes the active version; an approved version is never edited — a
 * revision is a new version that supersedes it when approved.
 *
 * Each line names the ledger accounts whose posted amounts are its actuals. What a line has used is
 * computed, never stored, so nothing can drift:
 *
 * - **committed** — approved purchase orders charged to the line, less what was already invoiced;
 * - **invoiced** — vendor invoices on those orders not yet posted to the ledger (no posting rule yet,
 *   BD-18), so spending never disappears between an order and its posting;
 * - **actual** — posted ledger lines on the line's accounts and dimension, in the fiscal year.
 *
 * A vendor invoice counts once: as invoiced until it is posted, as actual afterwards.
 *
 * What happens when a purchase would exceed the line is the client's policy (`BD-49`, setting
 * `finance.budgetControl`): refuse, warn, or require an overspend approval. Until it is chosen the
 * check runs, is recorded as `notConfigured` on the purchase, and enforces nothing — visibly, never
 * silently.
 */

export const BUDGET_DIMENSIONS = [
  'company',
  'project',
  'department',
  'campaign',
  'costCenter',
] as const;
export const BudgetDimensionKindSchema = z.enum(BUDGET_DIMENSIONS);
export type BudgetDimensionKind = z.infer<typeof BudgetDimensionKindSchema>;

export const BudgetDimensionSchema = z
  .strictObject({
    kind: BudgetDimensionKindSchema,
    projectId: RecordIdSchema.optional(),
    departmentId: RecordIdSchema.optional(),
    campaignId: RecordIdSchema.optional(),
    costCenterCode: BusinessCodeSchema.optional(),
  })
  .refine(
    (value) =>
      (value.kind === 'company' &&
        !value.projectId &&
        !value.departmentId &&
        !value.campaignId &&
        !value.costCenterCode) ||
      (value.kind === 'project' && !!value.projectId) ||
      (value.kind === 'department' && !!value.departmentId) ||
      (value.kind === 'campaign' && !!value.campaignId) ||
      (value.kind === 'costCenter' && !!value.costCenterCode),
    { message: 'BUDGET_DIMENSION_INVALID', path: ['kind'] },
  );
export type BudgetDimension = z.infer<typeof BudgetDimensionSchema>;

export const BUDGET_STATES = ['active', 'archived'] as const;
export const BudgetStateSchema = z.enum(BUDGET_STATES);

export const BUDGET_VERSION_STATES = [
  'draft',
  'pendingApproval',
  'approved',
  'rejected',
  'superseded',
  'withdrawn',
] as const;
export const BudgetVersionStateSchema = z.enum(BUDGET_VERSION_STATES);
export type BudgetVersionState = z.infer<typeof BudgetVersionStateSchema>;

const AmountSchema = DecimalStringSchema.refine((value) => !value.startsWith('-'), {
  message: 'AMOUNT_NEGATIVE',
});

export const BudgetLineInputSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  /** Ledger accounts whose posted amounts are this line's actuals (expense or asset accounts). */
  accountIds: z.array(RecordIdSchema).max(50).default([]),
  /** The annual amount. */
  amount: AmountSchema,
  /**
   * Optional monthly phasing — one amount per period of the fiscal year, in order; when given they
   * must add up exactly to the annual amount.
   */
  monthly: z.array(AmountSchema).max(12).optional(),
});
export type BudgetLineInput = z.infer<typeof BudgetLineInputSchema>;

export const BudgetLineSchema = BudgetLineInputSchema.extend({
  lineNo: z.number().int().positive(),
});
export type BudgetLine = z.infer<typeof BudgetLineSchema>;

export const BudgetVersionSchema = z.strictObject({
  versionId: RecordIdSchema,
  budgetId: RecordIdSchema,
  versionNo: z.number().int().positive(),
  state: BudgetVersionStateSchema,
  lines: z.array(BudgetLineSchema),
  total: MoneySchema,
  reason: z.string().optional(),
  approvalRequestId: z.string().optional(),
  preparedByAccountId: z.string(),
  submittedByAccountId: z.string().optional(),
  decidedAt: InstantSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type BudgetVersion = z.infer<typeof BudgetVersionSchema>;

export const BudgetSchema = z.strictObject({
  budgetId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  fiscalYearId: RecordIdSchema,
  fiscalYearCode: z.string(),
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema,
  currency: z.string(),
  dimension: BudgetDimensionSchema,
  ownerAccountId: z.string(),
  state: BudgetStateSchema,
  /** The approved version in force, when one is. */
  activeVersionNo: z.number().int().positive().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Budget = z.infer<typeof BudgetSchema>;

export const CreateBudgetSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  fiscalYearId: RecordIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  dimension: BudgetDimensionSchema,
});
export type CreateBudget = z.infer<typeof CreateBudgetSchema>;

export const SaveBudgetVersionSchema = z.strictObject({
  lines: z.array(BudgetLineInputSchema).min(1).max(200),
  reason: z.string().trim().min(3).max(500).optional(),
  /** The draft's version when editing it; absent to start a new draft. */
  expectedVersion: z.number().int().positive().optional(),
});
export type SaveBudgetVersion = z.infer<typeof SaveBudgetVersionSchema>;

export const BudgetVersionActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500).optional(),
});

export const BudgetQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  fiscalYearId: RecordIdSchema.optional(),
  state: BudgetStateSchema.optional(),
  kind: BudgetDimensionKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type BudgetQuery = z.infer<typeof BudgetQuerySchema>;

/* ----------------------------------------------------------- consumption */

export const BudgetLineUsageSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  budget: MoneySchema,
  committed: MoneySchema,
  invoiced: MoneySchema,
  actual: MoneySchema,
  /** budget − committed − invoiced − actual. Negative when overspent. */
  available: MoneySchema,
  /** budget − actual. */
  variance: MoneySchema,
  /** (committed + invoiced + actual) / budget × 100, two places; absent when the budget is zero. */
  usedPercent: z.string().optional(),
  level: z.enum(['normal', 'alert', 'exceeded']),
});
export type BudgetLineUsage = z.infer<typeof BudgetLineUsageSchema>;

export const BudgetUsageSchema = z.strictObject({
  budgetId: RecordIdSchema,
  versionNo: z.number().int().positive().optional(),
  currency: z.string(),
  lines: z.array(BudgetLineUsageSchema),
  totals: BudgetLineUsageSchema.omit({ lineNo: true, code: true, name: true, level: true }),
  /** Actuals come from posted entries only; invoices waiting for a posting rule count as invoiced. */
  basis: z.literal('postedLedgerAndOpenCommitments'),
  generatedAt: InstantSchema,
});
export type BudgetUsage = z.infer<typeof BudgetUsageSchema>;

/* --------------------------------------------- a period against the budget (2026-10-17) */

/**
 * How a budget's amount for a period is known: the whole fiscal year (the annual amount), the
 * months of its monthly phasing, or not at all — a part of a year without phasing, or a range that
 * does not follow whole months. A budget is never prorated by days: what is not phased is not shown.
 */
export const BUDGET_PERIOD_BASES = ['fullYear', 'phased', 'notPhased'] as const;

export const BudgetPeriodLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  accountIds: z.array(RecordIdSchema),
  /** Absent when the period's budget cannot be known (see `basis`). */
  budgeted: MoneySchema.optional(),
  /** Posted ledger amounts on the line's accounts and dimension, in the period. */
  actual: MoneySchema,
  /** budgeted − actual; negative when over budget. */
  variance: MoneySchema.optional(),
  usedPercent: z.string().optional(),
});
export type BudgetPeriodLine = z.infer<typeof BudgetPeriodLineSchema>;

export const BudgetPeriodEntrySchema = z.strictObject({
  budgetId: RecordIdSchema,
  code: z.string(),
  name: z.string(),
  legalEntityId: RecordIdSchema,
  currency: z.string(),
  versionNo: z.number().int().positive(),
  dimension: BudgetDimensionSchema,
  basis: z.enum(BUDGET_PERIOD_BASES),
  lines: z.array(BudgetPeriodLineSchema),
  totals: BudgetPeriodLineSchema.pick({
    budgeted: true,
    actual: true,
    variance: true,
    usedPercent: true,
  }),
});
export type BudgetPeriodEntry = z.infer<typeof BudgetPeriodEntrySchema>;

/* ------------------------------------------------------------- control (BD-49) */

/**
 * The client's budget control policy (`BD-49`). `null` = not configured: checks run and are shown as
 * `notConfigured`, nothing is refused.
 */
export const BudgetControlSchema = z
  .strictObject({
    /** What happens when a purchase would exceed what is available on its line. */
    onExceed: z.enum(['refuse', 'warn', 'approval']),
    /** Whether a purchase request must name a budget line at all. */
    requireBudgetLine: z.boolean(),
    /** Alert the budget owner when a line's use reaches this percentage (`G-07`); null = no alert. */
    alertPercent: z.number().int().min(1).max(100).nullable(),
  })
  .nullable();
export type BudgetControl = z.infer<typeof BudgetControlSchema>;

export const BudgetRefSchema = z.strictObject({
  budgetId: RecordIdSchema,
  lineNo: z.number().int().positive(),
});
export type BudgetRef = z.infer<typeof BudgetRefSchema>;

/** The outcome of a budget check, recorded on the purchase that asked. */
export const BUDGET_CHECK_STATUSES = [
  'notConfigured',
  'noBudgetLine',
  'withinBudget',
  'exceedsBudget',
  'overspendApproved',
] as const;

/** Exact percentage of `used` against `budget`, two places, or undefined when the budget is zero. */
export function usedPercent(used: Money, budget: Money): string | undefined {
  if (isZeroMoney(budget)) return undefined;
  return divideMoney(multiplyMoney(used, '100'), budget.amount, 2, 'halfUp').amount;
}

export const BUDGET_APPROVAL_OPERATIONS = {
  /** A budget version: proceeds without a policy (approved at once), waits where one governs it. */
  approve: 'finance.budget.approve',
  /** A purchase beyond its budget line when `onExceed` is `approval`: refused without a policy. */
  overspend: 'finance.budget.overspend',
} as const;

export const BUDGET_AUDIT_ACTIONS = {
  created: 'finance.budget.created',
  archived: 'finance.budget.archived',
  versionSaved: 'finance.budget.versionSaved',
  versionSubmitted: 'finance.budget.versionSubmitted',
  versionDecided: 'finance.budget.versionDecided',
  versionWithdrawn: 'finance.budget.versionWithdrawn',
} as const;
